import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Flashbar, FlashbarProps } from "@amzn/awsui-components-react";

/**
 * Bottom-right toasts, matching the folder notifications in useFolderUI (fixed Flashbar,
 * auto-dismissed). Cloudscape itself has no toast; this is the app's existing convention for
 * "it worked, carry on" feedback that should not push the page content down.
 *
 * Successes auto-dismiss; errors stay until dismissed so nothing important disappears unread.
 */

export const TOAST_STYLES = {
  position: "fixed",
  bottom: "2vh",
  right: "2vw",
  width: "30vw",
  minWidth: "300px",
  maxWidth: "600px",
  zIndex: 1000,
} as const;

export const SUCCESS_TOAST_MS = 6000;

export type ToastInput = Omit<
  FlashbarProps.MessageDefinition,
  "id" | "onDismiss"
>;

export interface ToastsApi {
  /** Rendered once, anywhere in the tree (it is position: fixed). */
  element: React.ReactNode;
  push: (toast: ToastInput) => void;
  /** Convenience: a persistent error toast with the standard header. */
  showError: (message: string) => void;
  /** Drop every toast (e.g. the context they described has changed). */
  clear: () => void;
}

export function useToasts(): ToastsApi {
  const [items, setItems] = useState<FlashbarProps.MessageDefinition[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    setItems((all) => all.filter((f) => f.id !== id));
  }, []);

  useEffect(
    () => () => {
      timers.current.forEach((t) => clearTimeout(t));
      timers.current.clear();
    },
    []
  );

  const push = useCallback(
    (toast: ToastInput) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setItems((all) => [
        {
          ...toast,
          id,
          dismissible: true,
          dismissLabel: "Dismiss",
          ariaRole: toast.type === "error" ? "alert" : "status",
          onDismiss: () => dismiss(id),
        },
        ...all,
      ]);
      if (toast.type !== "error") {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), SUCCESS_TOAST_MS)
        );
      }
    },
    [dismiss]
  );

  const clear = useCallback(() => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current.clear();
    setItems([]);
  }, []);

  const showError = useCallback(
    (message: string) =>
      push({ type: "error", header: "Action failed", content: message }),
    [push]
  );

  const element = useMemo(
    () =>
      items.length ? (
        <div style={TOAST_STYLES} data-testid="toasts">
          <Flashbar items={items} />
        </div>
      ) : null,
    [items]
  );

  return useMemo(
    () => ({ element, push, showError, clear }),
    [element, push, showError, clear]
  );
}
